import type { Product, OfferSummary, CompetitorOffer } from '../../types/repricer';

/**
 * Validates and transforms raw JSON data into Product format
 */
export function validateProduct(rawProduct: any): Product | null {
  // Check if rawProduct is a valid object
  if (!rawProduct || typeof rawProduct !== 'object') {
    console.warn('Product missing valid ASIN, skipping:', rawProduct);
    return null;
  }

  // Validate ASIN (required field)
  if (!rawProduct.asin || typeof rawProduct.asin !== 'string') {
    console.warn('Product missing valid ASIN, skipping:', rawProduct);
    return null;
  }

  const product: Product = {
    asin: rawProduct.asin,
    competitor_offers: [],
  };

  // Validate and add offer_summary if present
  if (rawProduct.offer_summary) {
    const offerSummary = validateOfferSummary(rawProduct.offer_summary);
    if (offerSummary) {
      product.offer_summary = offerSummary;
    }
  }

  // Validate and add competitor_offers
  if (Array.isArray(rawProduct.competitor_offers)) {
    product.competitor_offers = rawProduct.competitor_offers
      .map((offer: any) => validateCompetitorOffer(offer))
      .filter((offer: CompetitorOffer | null): offer is CompetitorOffer => offer !== null);
  }

  return product;
}

/**
 * Validates offer summary data
 */
function validateOfferSummary(rawSummary: any): OfferSummary | null {
  if (!rawSummary || typeof rawSummary !== 'object') {
    return null;
  }

  // Validate required numeric fields
  const lowestPrice = parseFloat(rawSummary.lowest_price);
  const totalOffers = parseFloat(rawSummary.total_offers);
  const featuredPrice = parseFloat(rawSummary.featured_price);

  if (isNaN(lowestPrice) || isNaN(totalOffers) || isNaN(featuredPrice)) {
    return null;
  }

  return {
    lowest_price: lowestPrice,
    total_offers: totalOffers,
    change_type: rawSummary.change_type || '',
    featured_price: featuredPrice,
    timestamp: rawSummary.timestamp || new Date().toISOString(),
  };
}

/**
 * Validates competitor offer data
 */
function validateCompetitorOffer(rawOffer: any): CompetitorOffer | null {
  if (!rawOffer || typeof rawOffer !== 'object') {
    return null;
  }

  // Validate required fields
  const price = parseFloat(rawOffer.price);
  const shipping = parseFloat(rawOffer.shipping);
  const feedbackRating = parseFloat(rawOffer.feedback_rating);
  const feedbackCount = parseFloat(rawOffer.feedback_count);

  // Skip offers with invalid numeric data
  if (isNaN(price) || isNaN(shipping) || isNaN(feedbackRating) || isNaN(feedbackCount)) {
    return null;
  }

  // Skip offers with missing seller_id
  if (!rawOffer.seller_id || typeof rawOffer.seller_id !== 'string') {
    return null;
  }

  return {
    shipping,
    price,
    is_buybox: Boolean(rawOffer.is_buybox),
    feedback_rating: feedbackRating,
    seller_id: rawOffer.seller_id,
    is_featured: Boolean(rawOffer.is_featured),
    feedback_count: feedbackCount,
  };
}

/**
 * Validates an array of products from raw JSON data
 */
export function validateProducts(rawData: any): Product[] {
  if (!Array.isArray(rawData)) {
    console.error('Invalid data format: expected array of products');
    return [];
  }

  const validProducts = rawData
    .map(validateProduct)
    .filter((product): product is Product => product !== null);

  console.log(`Validated ${validProducts.length} of ${rawData.length} products`);
  
  return validProducts;
}
